import { enhanceChartDropdowns, refreshChartDropdown } from "./chart-dropdown.js";

export const CLOCK_PREFERENCE_VERSION = 1;
export const CLOCK_STORAGE_KEY = `flexcharting.toolbar-clock.v${CLOCK_PREFERENCE_VERSION}`;

export const DEFAULT_CLOCK_SETTINGS = Object.freeze({
  dateFormat: "short",
  timeFormat: "12h",
  timeZone: "local"
});

export const DATE_FORMATS = Object.freeze([
  "short",
  "long",
  "month-day-year",
  "day-month-year",
  "iso",
  "hidden"
]);

export const TIME_FORMATS = Object.freeze([
  "12h",
  "12h-seconds",
  "24h",
  "24h-seconds"
]);

const FEATURED_TIME_ZONES = Object.freeze([
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo"
]);

export function normalizeClockSettings(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    dateFormat: DATE_FORMATS.includes(source.dateFormat)
      ? source.dateFormat
      : DEFAULT_CLOCK_SETTINGS.dateFormat,
    timeFormat: TIME_FORMATS.includes(source.timeFormat)
      ? source.timeFormat
      : DEFAULT_CLOCK_SETTINGS.timeFormat,
    timeZone: isValidClockTimeZone(source.timeZone) ? source.timeZone : "local"
  };
}

export function isValidClockTimeZone(value) {
  if (value === "local" || value === "UTC") return true;
  if (typeof value !== "string" || value.length === 0) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

export function clockShowsSeconds(settings) {
  return normalizeClockSettings(settings).timeFormat.endsWith("-seconds");
}

export function millisecondsUntilClockBoundary(date, showSeconds) {
  const instant = validDate(date);
  const interval = showSeconds ? 1_000 : 60_000;
  const remainder = ((instant.getTime() % interval) + interval) % interval;
  return remainder === 0 ? interval : interval - remainder;
}

export function formatToolbarClock(date, settings) {
  const instant = validDate(date);
  const normalized = normalizeClockSettings(settings);
  const dateText = formatClockDate(instant, normalized.dateFormat, normalized.timeZone);
  const timeText = formatClockTime(instant, normalized.timeFormat, normalized.timeZone);
  return dateText ? `${dateText} · ${timeText}` : timeText;
}

export function formatClockDate(date, format, timeZone = "local") {
  if (format === "hidden") return "";
  const instant = validDate(date);
  const zone = intlTimeZone(timeZone);
  if (format === "short" || format === "long") {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      year: "numeric",
      month: format === "short" ? "short" : "long",
      day: "numeric"
    }).format(instant);
  }

  const parts = numericDateParts(instant, zone);
  if (format === "day-month-year") return `${parts.day}/${parts.month}/${parts.year}`;
  if (format === "iso") return `${parts.year}-${parts.month}-${parts.day}`;
  return `${parts.month}/${parts.day}/${parts.year}`;
}

export function formatClockTime(date, format, timeZone = "local") {
  const instant = validDate(date);
  const showSeconds = format.endsWith("-seconds");
  const hour12 = format.startsWith("12h");
  const formatter = new Intl.DateTimeFormat(hour12 ? "en-US" : "en-GB", {
    timeZone: intlTimeZone(timeZone),
    hour: hour12 ? "numeric" : "2-digit",
    minute: "2-digit",
    second: showSeconds ? "2-digit" : undefined,
    hour12,
    hourCycle: hour12 ? "h12" : "h23"
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(instant)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  const seconds = showSeconds ? `:${parts.second}` : "";
  return hour12
    ? `${parts.hour}:${parts.minute}${seconds} ${parts.dayPeriod}`
    : `${parts.hour.padStart(2, "0")}:${parts.minute}${seconds}`;
}

export function currentUtcOffsetLabel(date, timeZone = "local") {
  const instant = validDate(date);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: intlTimeZone(timeZone),
    timeZoneName: "longOffset"
  });
  const offset = formatter.formatToParts(instant)
    .find((part) => part.type === "timeZoneName")?.value;
  if (!offset || offset === "GMT" || offset === "UTC") return "UTC+00:00";
  const normalized = offset.replace(/^GMT/, "UTC");
  const match = /^UTC([+-])(\d{1,2})(?::(\d{2}))?$/.exec(normalized);
  if (!match) return normalized;
  return `UTC${match[1]}${match[2].padStart(2, "0")}:${match[3] || "00"}`;
}

export function availableClockTimeZones() {
  const supported = typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : [];
  return [...new Set([...FEATURED_TIME_ZONES, ...supported])].sort((left, right) =>
    left.localeCompare(right)
  );
}

export function friendlyTimeZoneLabel(timeZone, date = new Date()) {
  const instant = validDate(date);
  if (timeZone === "local") {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone || "Browser default";
    return `System/local time — ${resolved} (${currentUtcOffsetLabel(instant, "local")})`;
  }
  if (timeZone === "UTC") return "UTC (UTC+00:00)";
  const friendly = timeZone.split("/").at(-1).replaceAll("_", " ");
  return `${friendly} — ${timeZone} (${currentUtcOffsetLabel(instant, timeZone)})`;
}

export class ClockPreferenceStore {
  constructor(storage = resolveBrowserStorage(), storageKey = CLOCK_STORAGE_KEY) {
    this.storage = storage;
    this.storageKey = String(storageKey || CLOCK_STORAGE_KEY);
  }

  load() {
    if (!this.storage) return { ...DEFAULT_CLOCK_SETTINGS };
    try {
      const parsed = JSON.parse(this.storage.getItem(this.storageKey));
      if (parsed?.version !== CLOCK_PREFERENCE_VERSION) {
        return { ...DEFAULT_CLOCK_SETTINGS };
      }
      return normalizeClockSettings(parsed);
    } catch {
      return { ...DEFAULT_CLOCK_SETTINGS };
    }
  }

  save(settings) {
    const normalized = normalizeClockSettings(settings);
    try {
      this.storage?.setItem(this.storageKey, JSON.stringify({
        version: CLOCK_PREFERENCE_VERSION,
        ...normalized
      }));
    } catch {
      // Storage can be disabled by browser policy. The in-memory setting still applies.
    }
    return normalized;
  }
}

export class AlignedClockScheduler {
  constructor({ onTick, now = () => new Date(), timers = globalThis }) {
    this.onTick = onTick;
    this.now = now;
    this.timers = timers;
    this.timer = null;
    this.running = false;
    this.showSeconds = false;
  }

  start(showSeconds = false) {
    this.stop();
    this.running = true;
    this.showSeconds = Boolean(showSeconds);
    const instant = validDate(this.now());
    this.onTick(instant);
    this.schedule(instant);
  }

  schedule(instant) {
    const delay = millisecondsUntilClockBoundary(instant, this.showSeconds);
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      if (!this.running) return;
      const next = validDate(this.now());
      this.onTick(next);
      this.schedule(next);
    }, delay);
  }

  stop() {
    this.running = false;
    if (this.timer !== null) this.timers.clearTimeout(this.timer);
    this.timer = null;
  }
}

export class DisposableEventListeners {
  constructor() {
    this.listeners = [];
  }

  add(target, type, listener, options) {
    target.addEventListener(type, listener, options);
    this.listeners.push({ target, type, listener, options });
  }

  dispose() {
    for (const entry of this.listeners.splice(0)) {
      entry.target.removeEventListener(entry.type, entry.listener, entry.options);
    }
  }
}

export class ToolbarClock {
  constructor({ element, store = new ClockPreferenceStore(), now, timers } = {}) {
    if (!element) throw new Error("The toolbar clock root was not found.");
    this.element = element;
    this.document = element.ownerDocument;
    this.window = this.document.defaultView;
    this.button = requiredElement("[data-clock-button]", element);
    this.display = requiredElement("[data-clock-display]", element);
    this.popover = requiredElement("[data-clock-popover]", element);
    this.preview = requiredElement("[data-clock-preview]", element);
    this.dateFormat = requiredElement("[data-clock-date-format]", element);
    this.timeFormat = requiredElement("[data-clock-time-format]", element);
    this.timeZoneSearch = requiredElement("[data-clock-time-zone-search]", element);
    this.timeZone = requiredElement("[data-clock-time-zone]", element);
    this.store = store;
    this.settings = store.load();
    this.now = now || (() => new Date());
    this.listeners = new DisposableEventListeners();
    this.destroyed = false;
    this.scheduler = new AlignedClockScheduler({
      onTick: (instant) => this.render(instant),
      now: this.now,
      timers: timers || this.window
    });

    this.dateFormat.value = this.settings.dateFormat;
    this.timeFormat.value = this.settings.timeFormat;
    this.renderTimeZoneOptions("", this.now());
    for (const select of [this.dateFormat, this.timeFormat, this.timeZone]) {
      select.dataset.chartDropdownPlacement = "top";
    }
    this.dropdowns = enhanceChartDropdowns(this.popover);
    this.dateFormatDropdown = this.dropdownFor(this.dateFormat);
    this.timeFormatDropdown = this.dropdownFor(this.timeFormat);
    this.timeZoneDropdown = this.dropdownFor(this.timeZone);
    this.bindEvents();
    this.scheduler.start(clockShowsSeconds(this.settings));
  }

  bindEvents() {
    this.listeners.add(this.button, "click", () => this.toggle());
    this.listeners.add(this.dateFormat, "change", () => {
      this.applySettings({ dateFormat: this.dateFormat.value });
    });
    this.listeners.add(this.timeFormat, "change", () => {
      this.applySettings({ timeFormat: this.timeFormat.value });
    });
    this.listeners.add(this.timeZone, "change", () => {
      if (this.timeZone.value) this.applySettings({ timeZone: this.timeZone.value });
    });
    this.listeners.add(this.timeZoneSearch, "input", () => {
      this.renderTimeZoneOptions(this.timeZoneSearch.value, this.now());
    });
    this.listeners.add(this.popover, "keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      this.close({ restoreFocus: true });
    }, true);
    this.listeners.add(this.button, "keydown", (event) => {
      if (event.key !== "Escape" || this.popover.hidden) return;
      event.preventDefault();
      this.close({ restoreFocus: true });
    });
    this.listeners.add(this.document, "pointerdown", (event) => {
      if (
        !this.popover.hidden
        && event.target instanceof this.window.Node
        && !this.element.contains(event.target)
      ) this.close();
    });
    this.listeners.add(this.window, "resize", () => {
      if (!this.popover.hidden) this.positionPopover();
    });
  }

  applySettings(patch) {
    this.settings = this.store.save({ ...this.settings, ...patch });
    this.dateFormat.value = this.settings.dateFormat;
    this.timeFormat.value = this.settings.timeFormat;
    if (this.timeZone.value !== this.settings.timeZone) {
      this.renderTimeZoneOptions(this.timeZoneSearch.value, this.now());
    }
    this.scheduler.start(clockShowsSeconds(this.settings));
  }

  render(instant) {
    const text = formatToolbarClock(instant, this.settings);
    this.display.textContent = text;
    this.display.dateTime = instant.toISOString();
    this.preview.textContent = text;
    this.button.setAttribute("aria-label", `Clock settings. Current date and time: ${text}`);
  }

  renderTimeZoneOptions(query, instant) {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const values = ["local", "UTC", ...availableClockTimeZones()];
    if (!values.includes(this.settings.timeZone)) values.splice(2, 0, this.settings.timeZone);
    const matching = values.filter((value) => {
      if (value === this.settings.timeZone) return true;
      if (!normalizedQuery) return true;
      return friendlyTimeZoneLabel(value, instant).toLocaleLowerCase().includes(normalizedQuery);
    });

    this.timeZone.replaceChildren();
    for (const value of matching) {
      const option = this.document.createElement("option");
      option.value = value;
      option.textContent = friendlyTimeZoneLabel(value, instant);
      this.timeZone.append(option);
    }
    if (matching.length === 0) {
      const empty = this.document.createElement("option");
      empty.textContent = "No matching time zones";
      empty.disabled = true;
      this.timeZone.append(empty);
    }
    this.timeZone.value = this.settings.timeZone;
    if (this.timeZoneDropdown) refreshChartDropdown(this.timeZone);
  }

  toggle() {
    if (this.popover.hidden) this.open();
    else this.close();
  }

  open() {
    this.timeZoneSearch.value = "";
    this.renderTimeZoneOptions("", this.now());
    this.popover.hidden = false;
    this.button.setAttribute("aria-expanded", "true");
    this.positionPopover();
    this.dateFormatDropdown.trigger.focus();
  }

  close({ restoreFocus = false } = {}) {
    if (this.popover.hidden) return;
    for (const dropdown of this.dropdowns) dropdown.close();
    this.popover.hidden = true;
    this.button.setAttribute("aria-expanded", "false");
    if (restoreFocus) this.button.focus({ preventScroll: true });
  }

  positionPopover() {
    const inset = 8;
    const gap = 7;
    const buttonBounds = this.button.getBoundingClientRect();
    const width = Math.max(160, Math.min(320, this.window.innerWidth - inset * 2));
    const maxHeight = Math.max(160, buttonBounds.top - inset - gap);
    this.popover.style.width = `${width}px`;
    this.popover.style.maxHeight = `${maxHeight}px`;
    const popoverBounds = this.popover.getBoundingClientRect();
    const left = clamp(
      buttonBounds.right - width,
      inset,
      Math.max(inset, this.window.innerWidth - width - inset)
    );
    const top = Math.max(inset, buttonBounds.top - popoverBounds.height - gap);
    this.popover.style.left = `${Math.round(left)}px`;
    this.popover.style.top = `${Math.round(top)}px`;
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scheduler.stop();
    this.listeners.dispose();
    this.close();
    for (const dropdown of this.dropdowns) dropdown.destroy();
  }

  dropdownFor(select) {
    const dropdown = this.dropdowns.find((candidate) => candidate.select === select);
    if (!dropdown) throw new Error("A toolbar clock dropdown could not be initialized.");
    return dropdown;
  }
}

function numericDateParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return Object.fromEntries(
    formatter.formatToParts(date)
      .filter((part) => ["year", "month", "day"].includes(part.type))
      .map((part) => [part.type, part.value])
  );
}

function intlTimeZone(timeZone) {
  return timeZone === "local" ? undefined : timeZone;
}

function validDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) throw new RangeError("A valid date is required.");
  return date;
}

function resolveBrowserStorage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

function requiredElement(selector, scope) {
  const element = scope.querySelector(selector);
  if (!element) throw new Error(`Required toolbar clock element not found: ${selector}`);
  return element;
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}
